import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, Check, User, Users, X } from 'lucide-react';

export interface AssistantProvider {
  id: string;
  name: string;
  role: string;
  title?: string;
  email?: string;
  supervisor_id?: string | null;
  requires_supervision?: boolean | number;
}

interface SupervisorAssistantSelectorProps {
  assistants: AssistantProvider[];
  selectedAssistantId: string; // 'ALL' or provider ID
  onSelectAssistant: (assistantId: string) => void;
  isOwnerOrAdmin?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  hideTriggerCount?: boolean;
  className?: string;
  lightTheme?: boolean;
}

export const SupervisorAssistantSelector: React.FC<SupervisorAssistantSelectorProps> = ({
  assistants,
  selectedAssistantId,
  onSelectAssistant,
  isOwnerOrAdmin = false,
  disabled = false,
  hideLabel = false,
  hideTriggerCount = false,
  className = '',
  lightTheme = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownCoords, setDropdownCoords] = useState<{ top: number; left: number; width: number } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Measure and align dropdown precisely beneath the trigger
  const updateCoords = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownCoords({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width
    });
  };

  // Re-measure on resize or scroll while open
  useEffect(() => {
    if (!isOpen) return;

    updateCoords();

    const handleScrollOrResize = () => {
      updateCoords();
    };

    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, true);

    return () => {
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
    };
  }, [isOpen]);

  // Close when clicking outside or pressing Escape
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('keydown', handleKeyDown);
      // Focus search input on open
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const selectedAssistant = useMemo(() => {
    if (selectedAssistantId === 'ALL') return null;
    return assistants.find(a => a.id === selectedAssistantId) || null;
  }, [assistants, selectedAssistantId]);

  // Filtered assistants by search query (name, provider ID, email)
  const filteredAssistants = useMemo(() => {
    if (!searchQuery.trim()) return assistants;
    const q = searchQuery.toLowerCase().trim();
    return assistants.filter(a =>
      a.name.toLowerCase().includes(q) ||
      a.id.toLowerCase().includes(q) ||
      (a.email && a.email.toLowerCase().includes(q)) ||
      (a.title && a.title.toLowerCase().includes(q))
    );
  }, [assistants, searchQuery]);

  const allLabel = isOwnerOrAdmin ? 'All Assistants' : 'All My Assistants';

  return (
    <div className={`relative inline-flex flex-col text-left ${className || 'w-full sm:w-[260px] sm:shrink-0'}`} ref={containerRef}>
      {/* Label above control */}
      {!hideLabel && (
        <span className="text-[9px] font-mono uppercase font-bold tracking-wider text-slate-400 mb-0.5 px-0.5">
          Assistant
        </span>
      )}

      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!isOpen) {
            updateCoords();
          }
          setIsOpen(!isOpen);
        }}
        className={`w-full h-8 px-2.5 rounded-lg border text-xs font-mono font-medium flex items-center justify-between gap-2 transition-all cursor-pointer select-none ${
          isOpen
            ? 'border-slate-400 bg-slate-100 text-slate-950 shadow-xs'
            : selectedAssistantId !== 'ALL'
            ? 'border-slate-300 bg-white text-slate-900 shadow-xs hover:bg-slate-50 hover:border-slate-400/70'
            : 'border-slate-300 bg-white hover:bg-slate-50 hover:border-slate-400/70 text-slate-900 shadow-xs'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div className="flex items-center space-x-1.5 truncate min-w-0">
          {selectedAssistant ? (
            <>
              <div className="w-1.5 h-1.5 rounded-full shrink-0 bg-slate-700" />
              <span className="font-semibold truncate text-slate-900">
                {selectedAssistant.name}
              </span>
              <span className="text-[9.5px] font-bold px-1 py-0.2 rounded shrink-0 bg-sky-50 text-sky-800 border border-sky-200">
                SLPA
              </span>
            </>
          ) : (
            <>
              <Users className="w-3.5 h-3.5 shrink-0 text-slate-600" />
              <span className="truncate text-slate-900 font-semibold">
                {allLabel}
              </span>
              {!hideTriggerCount && (
                <span className="text-[9.5px] font-mono shrink-0 text-slate-500 font-semibold">
                  ({assistants.length})
                </span>
              )}
            </>
          )}
        </div>
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform duration-150 ${
          isOpen ? 'rotate-180 text-slate-700' : 'text-slate-500'
        }`} />
      </button>

      {/* Floating Popover Dropdown rendered via React Portal to document.body */}
      {isOpen && dropdownCoords && typeof document !== 'undefined' && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: `${dropdownCoords.top}px`,
            left: `${dropdownCoords.left}px`,
            width: `${dropdownCoords.width}px`,
            zIndex: 9999
          }}
          className="rounded-xl border border-slate-200 p-2 space-y-1.5 animate-fadeIn bg-white shadow-[0_10px_25px_rgba(15,23,42,0.12)]"
        >
          {/* Internal Search Input */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search assistant..."
              className="w-full pl-8 pr-7 py-1.5 rounded-lg text-xs font-sans focus:outline-none bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-slate-400"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-700"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Assistant Options Roster (Internally Scrollable, Unconstrained by Header Boundary) */}
          <div className="max-h-60 overflow-y-auto space-y-0.5 pr-0.5 custom-scrollbar" role="listbox">
            {/* "All My Assistants" Option */}
            <button
              type="button"
              onClick={() => {
                onSelectAssistant('ALL');
                setIsOpen(false);
                setSearchQuery('');
              }}
              className={`w-full px-2 py-1.5 rounded-lg text-left text-xs font-mono flex items-center justify-between transition-colors ${
                selectedAssistantId === 'ALL'
                  ? 'bg-slate-100 text-slate-900 border border-slate-300 font-semibold'
                  : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950'
              }`}
              role="option"
              aria-selected={selectedAssistantId === 'ALL'}
            >
              <div className="flex items-center space-x-2 truncate min-w-0">
                <Users className="w-3.5 h-3.5 shrink-0 text-slate-600" />
                <span className="truncate">{allLabel}</span>
                <span className="text-[10px] shrink-0 text-slate-500 font-semibold">({assistants.length})</span>
              </div>
              {selectedAssistantId === 'ALL' && (
                <Check className="w-3.5 h-3.5 shrink-0 ml-1.5 text-slate-800" />
              )}
            </button>

            <div className="h-[1px] my-1 bg-slate-200" />

            {/* Individual Assistant Rows */}
            {filteredAssistants.length === 0 ? (
              <div className="py-3 text-center text-xs text-slate-500 font-sans">
                No assistants match &ldquo;{searchQuery}&rdquo;
              </div>
            ) : (
              filteredAssistants.map((assistant) => {
                const isSelected = selectedAssistantId === assistant.id;
                return (
                  <button
                    key={assistant.id}
                    type="button"
                    onClick={() => {
                      onSelectAssistant(assistant.id);
                      setIsOpen(false);
                      setSearchQuery('');
                    }}
                    className={`w-full px-2 py-1.5 rounded-lg text-left text-xs transition-colors flex items-center justify-between ${
                      isSelected
                        ? 'bg-slate-100 text-slate-900 border border-slate-300 font-semibold'
                        : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950'
                    }`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <div className="flex items-center space-x-2 truncate min-w-0">
                      <User className="w-3.5 h-3.5 shrink-0 text-slate-600" />
                      <div className="truncate min-w-0">
                        <div className="font-semibold truncate flex items-center space-x-1.5 text-slate-900">
                          <span className="truncate">{assistant.name}</span>
                          <span className="text-[9px] font-mono font-bold px-1 py-0.2 rounded shrink-0 bg-sky-50 text-sky-800 border border-sky-200">
                            {assistant.role || 'SLPA'}
                          </span>
                        </div>
                        <div className="text-[10px] font-mono truncate text-slate-500">
                          {assistant.id}{assistant.email ? ` • ${assistant.email}` : ''}
                        </div>
                      </div>
                    </div>
                    {isSelected && (
                      <Check className="w-3.5 h-3.5 shrink-0 ml-1.5 text-slate-800" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
